import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, FileText, CheckCircle2, ChevronRight, ChevronLeft } from 'lucide-react';
import { formatCurrency, formatPercent } from '../../utils/localeFormatters';

/**
 * Smart insights panel for the Learning dashboard.
 *
 * Three stacked cards:
 *  - Students needing attention (low attendance)
 *  - Overdue payments (count + total)
 *  - Active surveys
 *
 * All insights derive from already-fetched dashboard data via
 * useDashboardInsights — no extra API calls.
 */
export default function DashboardInsights({ insights }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  const lowAttendance = insights?.lowAttendance || [];
  const overdue = insights?.overdue || [];
  const overdueTotal = insights?.overdueTotal || 0;
  const activeSurveys = insights?.activeSurveys || [];

  const rows = [
    lowAttendance.length > 0 && {
      key: 'lowAttendance',
      icon: AlertTriangle,
      tone: 'warning',
      title: t('learningDashboard.insights.lowAttendance'),
      desc: t('learningDashboard.insights.lowAttendanceDesc', { count: lowAttendance.length }),
      onClick: () => navigate('/students'),
    },
    overdue.length > 0 && {
      key: 'overdue',
      icon: CreditCard,
      tone: 'danger',
      title: t('learningDashboard.insights.overduePayments'),
      desc: t('learningDashboard.insights.overduePaymentsDesc', {
        count: overdue.length,
        amount: formatCurrency(overdueTotal, i18n.language, 'ILS'),
      }),
      onClick: () => navigate('/jibaya'),
    },
    activeSurveys.length > 0 && {
      key: 'surveys',
      icon: FileText,
      tone: 'info',
      title: t('learningDashboard.insights.activeSurveys'),
      desc: t('learningDashboard.insights.activeSurveysDesc', { count: activeSurveys.length }),
      onClick: () => navigate('/surveys'),
    },
  ].filter(Boolean);

  const toneStyles = {
    warning: { fg: 'var(--color-warning-dark, #B45309)', bg: 'rgba(245, 158, 11, 0.10)' },
    danger: { fg: 'var(--color-danger-dark, #B91C1C)', bg: 'rgba(239, 68, 68, 0.10)' },
    info: { fg: '#0369A1', bg: 'rgba(14, 165, 233, 0.10)' },
  };

  return (
    <aside
      className="dashboard-insights"
      aria-labelledby="insights-title"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h3
          id="insights-title"
          style={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          {t('learningDashboard.insights.title')}
        </h3>
        {insights?.currentAttendanceRate != null && (
          <span
            aria-label={t('learningDashboard.kpi.attendanceRate')}
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              backgroundColor: 'var(--color-surface-alt)',
              padding: '0.25rem 0.625rem',
              borderRadius: '999px',
            }}
          >
            {formatPercent(insights.currentAttendanceRate, i18n.language)}
          </span>
        )}
      </header>

      {rows.length === 0 ? (
        <div
          style={{
            padding: '2.5rem 1.25rem',
            textAlign: 'center',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <CheckCircle2 size={32} color="var(--color-primary-dark)" style={{ opacity: 0.6 }} />
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            {t('learningDashboard.insights.empty')}
          </p>
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {rows.map((r, idx) => {
            const tone = toneStyles[r.tone];
            return (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={r.onClick}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.875rem',
                    padding: '0.875rem 1.25rem',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'start',
                    borderBlockStart: idx === 0 ? 'none' : '1px solid var(--color-border)',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      backgroundColor: tone.bg,
                      color: tone.fg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <r.icon size={18} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {r.title}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        color: 'var(--color-text-secondary)',
                        marginBlockStart: 2,
                      }}
                    >
                      {r.desc}
                    </span>
                  </span>
                  <Chevron size={16} color="var(--color-text-tertiary)" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
